import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import {
  ArrowBack,
  ArrowForward,
  AutoAwesome,
  Clear,
  FilterList,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { InfoOutlined } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { useNavigation, useNotification } from "@refinedev/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSafeApiCall } from "../utils/apiHelper";
import "react-medium-image-zoom/dist/styles.css";
import { useParams, useSearchParams } from "react-router-dom";
import {
  createCustomBulletPoint,
  createDetailBulletPoint,
  createJobBackup,
  generateBulletPointDescription,
  getAdvancedStats,
  getBatchById,
  getJobBulletPoints,
  getJobById,
  getJobImages,
  updateJobStatus,
} from "../api/batches";
import BulletPointsList from "../components/editor/bullet-points-list";
import ImageZoom from "../components/editor/image-zoom";
import {
  Batch,
  BatchAdvancedStats,
  BrandBatchSettings,
  BulletPoint,
  BulletPointType,
  colorByStatus,
  Job,
  JobStatus,
  SheetData,
  SheetDataItem,
  statusToLabel,
} from "./playground/types";
import EditorPhrasebookEditor from "../components/editor/phrasebook/editor";
import usePhrasebookStore from "../components/editor/phrasebook/store";
import EditorStatus from "../components/editor/status";
import {
  getBrandsWithGlobalPhrases,
  getJobPhrases,
  Phrase,
  PhraseStatus,
  PhraseType,
  unifiedJobPhrasesOperations,
} from "../api/phrasebook";
import { getGroupById } from "../utils/content-groups";
import EditorPhrasebookSearch from "../components/editor/phrasebook/search";
import { useAuthStore } from "../authProvider";
import MasterFileView from "../components/editor/master-file-view";
import Tracker from "../components/editor/tracker";
import EditorGeneration from "../components/editor/generation";
import logo from "../assets/logo_transparent_black.png";

let imageEnterTimeout: NodeJS.Timeout | null = null;

export default function Editor({
  batchId,
  jobId,
  backToBatchUrl,
  previousJobUrl,
  nextJobUrl,
  sheetData,
}: {
  batchId?: string;
  jobId?: string;
  backToBatchUrl?: string;
  previousJobUrl?: string;
  nextJobUrl?: string;
  sheetData?: SheetData;
}) {
  const { open } = useNotification();

  const params = useParams();

  if (!batchId || !jobId) {
    batchId = params.batchId;
    jobId = params.jobId;
  }

  const navigation = useNavigation();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();

  // Get active filters from URL params
  const statusFilter = searchParams.get("status");
  const columnFilter = searchParams.get("column");
  const keywordQuery = searchParams.get("keyword_query");
  const keywordScope = searchParams.get("keyword_scope");

  // Keyword search and normal filters are mutually exclusive
  const isKeywordSearchMode = !!keywordQuery;

  // Build active filters list for tooltip
  const activeFilters: string[] = [];
  if (isKeywordSearchMode) {
    // Only show keyword search filter
    activeFilters.push(
      `Keyword: "${keywordQuery}" in ${
        keywordScope === "brand" ? "Brand" : "This Batch"
      }`
    );
  } else {
    // Only show normal filters
    if (statusFilter) {
      activeFilters.push(`Status: ${statusFilter.replace(/_/g, " ")}`);
    }
    if (columnFilter) {
      activeFilters.push(`Column: ${columnFilter}`);
    }
  }
  const hasActiveFilters = activeFilters.length > 0;

  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );

  const {
    activeField,
    setActiveField,
    searchTab,
    hasChanges,
    setHasChanges,
    setGarmentType,
    setSavingPhrases,
    savingPhrases,
    setRefreshSearch,
    isGenerating,
    setIsGenerating,
    clearSearch,
    clearGeneratedContent,
  } = usePhrasebookStore();

  // Handle page close/refresh with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        // Standard way to show a confirmation dialog when closing the browser
        const message =
          "There are unsaved changes. Are you sure you want to exit?";
        e.preventDefault();
        e.returnValue = message; // Required for Chrome
        return message; // For older browsers
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasChanges]);

  /** State block */

  const [job, setJob] = useState<Job | null>(null);
  const [batch, setBatch] = useState<Batch | null>(null);
  const [advancedStats, setAdvancedStats] = useState<BatchAdvancedStats | null>(
    null
  );
  const [bulletPoints, setBulletPoints] = useState<Array<BulletPoint>>([]);
  const [images, setImages] = useState<string[]>([]);
  const [jobPhrases, setJobPhrases] = useState<Phrase[]>([]);
  // Track original phrase IDs and their types for deletion detection
  const [originalPhrases, setOriginalPhrases] = useState<Map<string, PhraseType>>(new Map());

  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null
  );
  const [statusModified, setStatusModified] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [bulletPointToCopy, setBulletPointToCopy] = useState<string | null>(
    null
  );

  const [brandsWithGlobalPhrases, setBrandsWithGlobalPhrases] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);

  // Custom bullet point dialog state
  const [showCustomBpDialog, setShowCustomBpDialog] = useState(false);
  const [customBpName, setCustomBpName] = useState("");
  const [customBpDescription, setCustomBpDescription] = useState("");
  const [isGeneratingCustomBpDescription, setIsGeneratingCustomBpDescription] =
    useState(false);

  /** Refresh calls block */

  const safeApiCall = useSafeApiCall();

  const refreshJob = useCallback(async () => {
    await safeApiCall(getJobById, [String(jobId)], setJob, {
      errorMessage: "Error fetching job",
    });
  }, [jobId, safeApiCall]);

  const refreshBatch = useCallback(async () => {
    await safeApiCall(getBatchById, [String(batchId)], setBatch, {
      errorMessage: "Error fetching batch",
    });
  }, [batchId, safeApiCall]);

  const refreshAdvancedStats = useCallback(async () => {
    if (user?.isClientRelated) {
      return;
    }

    await safeApiCall(
      getAdvancedStats,
      [{ batchId: String(batchId) }],
      setAdvancedStats,
      {
        errorMessage: "Error fetching advanced stats",
      }
    );
  }, [batchId, safeApiCall, user]);

  const refreshBulletPoints = useCallback(async () => {
    await safeApiCall(getJobBulletPoints, [String(jobId)], setBulletPoints, {
      errorMessage: "Error fetching bullet points",
    });
  }, [jobId, safeApiCall]);

  const refreshImages = useCallback(async () => {
    await safeApiCall(getJobImages, [{ jobId: String(jobId) }], setImages, {
      suppressErrorNotification: true,
    });
  }, [jobId, safeApiCall]);

  const refreshJobPhrases = useCallback(async () => {
    await safeApiCall(
      getJobPhrases,
      [{ jobId: String(jobId) }],
      (phrases: Phrase[]) => {
        setJobPhrases(phrases);
        // Track which phrase IDs existed at load time with their types
        const phraseMap = new Map<string, PhraseType>();
        phrases.filter(p => p.id && p.type).forEach(p => {
          phraseMap.set(p.id!, p.type as PhraseType);
        });
        setOriginalPhrases(phraseMap);
      },
      {
        errorMessage: "Error fetching job phrases",
      }
    );
  }, [jobId, safeApiCall]);

  const refreshBrandsWithGlobalPhrases = useCallback(async () => {
    await safeApiCall(
      getBrandsWithGlobalPhrases,
      [],
      setBrandsWithGlobalPhrases,
      {
        errorMessage: "Error fetching brands with global phrases",
      }
    );
  }, [jobId, job?.batch_id]);

  useEffect(() => {
    refreshBrandsWithGlobalPhrases();
  }, [jobId, job?.batch_id]);

  /** useEffect block */

  useEffect(() => {
    if (!jobId || !batchId) return;

    setJob(null);
    setBatch(null);
    setAdvancedStats(null);
    setBulletPoints([]);
    setImages([]);
    setJobPhrases([]);
    setIsGenerating(false);

    clearSearch();
    clearGeneratedContent();

    setStatusModified(false);

    refreshJob();
    refreshBatch();
    refreshAdvancedStats();
    refreshBulletPoints();
    refreshImages();
    refreshJobPhrases();
  }, [jobId, batchId]);

  /** Event listeners block */

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Get the currently focused element
      const activeElement = document.activeElement;
      const tagName = activeElement?.tagName.toLowerCase();

      // Check if we're in an input field, textarea, or other interactive element
      const isInteractiveElement =
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        activeElement?.getAttribute("contenteditable") === "true";

      if (event.key === "Escape") {
        setSelectedImageIndex(null);
        setActiveField(null);
      } else if (!isInteractiveElement) {
        // Only handle number keys when not in an interactive element
        const numKey = parseInt(event.key);
        if (!isNaN(numKey) && numKey >= 1 && numKey <= 9) {
          const imageIndex = numKey - 1; // Convert to 0-based index

          // Only set the index if that image exists
          if (images.length > imageIndex) {
            if (selectedImageIndex === imageIndex) {
              setSelectedImageIndex(null);
            } else {
              setSelectedImageIndex(imageIndex);
            }
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [images.length, selectedImageIndex, setSelectedImageIndex]);

  // useEffect(() => {
  //   const processJobStatus = async () => {
  //     if (job && job?.status) {
  //       if (
  //         job?.status === JobStatus.TO_BE_STARTED &&
  //         !statusModified &&
  //         user?.isCopywriter
  //       ) {
  //         await updateJobStatus({
  //           id: String(jobId),
  //           status: JobStatus.IN_PROGRESS,
  //         });

  //         refreshJob();
  //         refreshBatch();

  //         setStatusModified(true);
  //       }
  //     }
  //   };

  //   processJobStatus();
  // }, [jobId, job?.status]);

  const bottomContainerRef = useRef<HTMLDivElement>(null);
  const [bottomContainerHeight, setBottomContainerHeight] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (!bottomContainerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setBottomContainerHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(bottomContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [bottomContainerRef.current]);

  /** Evaluation block */

  const batchBulletPointsOrder: BulletPointType[] =
    batch?.bullet_points_order || [];

  bulletPoints.sort((a, b) => {
    const aIndex = batchBulletPointsOrder.indexOf(a.type);
    const bIndex = batchBulletPointsOrder.indexOf(b.type);

    // If both types are in the order list, compare their positions
    if (aIndex >= 0 && bIndex >= 0) {
      return aIndex - bIndex;
    }

    // If a is not in the order list but b is, a should go at the bottom
    if (aIndex === -1 && bIndex !== -1) {
      return 1;
    }

    // If b is not in the order list but a is, b should go at the bottom
    if (bIndex === -1 && aIndex !== -1) {
      return -1;
    }

    // If neither is in the order list, maintain their original order
    return 0;
  });

  const cleanedUpBulletPoints = bulletPoints.map((bullet) => {
    return {
      ...bullet,
      variants: (bullet.variants || []).filter(
        (variant) =>
          variant.toLowerCase() !== "n/a" &&
          variant.toLowerCase() !== "false" &&
          variant.toLowerCase() !== ""
      ),
    };
  });

  const detailBulletPoints = cleanedUpBulletPoints.filter(
    (bullet) => bullet.type === BulletPointType.DETAIL
  );

  const customBulletPoints = cleanedUpBulletPoints.filter(
    (bullet) => bullet.type === BulletPointType.CUSTOM
  );

  const notDetailBulletPoints = cleanedUpBulletPoints.filter(
    (bullet) =>
      bullet.type !== BulletPointType.DETAIL &&
      bullet.type !== BulletPointType.CUSTOM
  );

  const garmentTypeBulletPoint = notDetailBulletPoints.find(
    (bullet) => bullet.type === BulletPointType.GARMENT_TYPE
  );

  const garmentTypeFromBulletPoint = garmentTypeBulletPoint?.variants?.[0];
  const garmentTypeApproved = garmentTypeBulletPoint?.approved || false;

  useEffect(() => {
    // Always update the garment type and approval status from bullet points
    setGarmentType(garmentTypeFromBulletPoint || "", garmentTypeApproved);
  }, [garmentTypeFromBulletPoint, garmentTypeApproved, setGarmentType]);

  // First, collect all bullet points by type
  const unorderedBulletPointsByType = notDetailBulletPoints.reduce(
    (acc, bullet) => {
      if (bullet.type !== BulletPointType.DETAIL) {
        acc[bullet.type] = acc[bullet.type] || [];
        acc[bullet.type].push(bullet);
      }
      return acc;
    },
    {} as Record<string, BulletPoint[]>
  );

  // Then create a new object with entries ordered according to the enum
  const bulletPointsByType = Object.values(BulletPointType).reduce(
    (acc, type) => {
      // Only include types that actually have bullet points (exclude DETAIL and CUSTOM which are shown separately)
      if (
        type !== BulletPointType.DETAIL &&
        type !== BulletPointType.CUSTOM &&
        unorderedBulletPointsByType[type]?.length > 0
      ) {
        acc[type] = unorderedBulletPointsByType[type];
      }
      return acc;
    },
    {} as Record<string, BulletPoint[]>
  );

  const batchSettings: BrandBatchSettings = batch?.settings || {};

  const updatePhrasesByType = (type: PhraseType, phrases: Phrase[]) => {
    const newPhrases = [...jobPhrases];

    const filteredPhrases = newPhrases.filter(
      (phrase) => phrase && phrase.type !== type
    );

    filteredPhrases.push(...phrases.map((phrase) => ({ ...phrase, type })));

    setJobPhrases(filteredPhrases);
  };

  const defaultSelectedBrands = batchSettings?.phrasebook_brands || [];

  const handleNextJobClick = useCallback(
    (skipHasChanges?: boolean) => {
      if (!nextJobUrl) return;

      if (hasChanges && !skipHasChanges) {
        setPendingNavigation(nextJobUrl);
        setShowNavigationModal(true);
      } else {
        navigation.push(nextJobUrl);
      }
    },
    [
      nextJobUrl,
      hasChanges,
      navigation,
      setPendingNavigation,
      setShowNavigationModal,
    ]
  );

  const handlePreviousJobClick = useCallback(() => {
    if (!previousJobUrl) return;

    if (hasChanges) {
      setPendingNavigation(previousJobUrl);
      setShowNavigationModal(true);
    } else {
      navigation.push(previousJobUrl);
    }
  }, [
    previousJobUrl,
    hasChanges,
    navigation,
    setPendingNavigation,
    setShowNavigationModal,
  ]);

  const handleSavePhrases = async (): Promise<boolean> => {
    if (!jobId) return false;

    setSavingPhrases(true);

    const phrasesGroupedByType = jobPhrases.reduce((acc, phrase) => {
      if (!phrase.type) return acc;

      acc[phrase.type] = acc[phrase.type] || [];
      acc[phrase.type].push(phrase);

      return acc;
    }, {} as Record<PhraseType, Phrase[]>);

    try {
      const creates: Array<{
        phrase: string;
        job_id: string;
        type: PhraseType;
        order: number;
        is_global?: boolean;
        status?: PhraseStatus;
      }> = [];
      const updates: Array<{
        phrase_id: string;
        phrase: string;
        job_id: string;
        type: PhraseType;
        order: number;
        is_global?: boolean;
        status?: PhraseStatus;
      }> = [];
      const deletes: Array<{
        job_id: string;
        phrase_id: string;
        type: PhraseType;
      }> = [];

      Object.entries(phrasesGroupedByType).forEach(([, phrases]) => {
        const phrasesArray = phrases as Phrase[];

        phrasesArray.forEach((phrase, index) => {
          if (!phrase.type) return;

          const isEmptyPhrase = !phrase.phrase || phrase.phrase.trim() === "";

          if (phrase.id) {
            // Get maxPhrases from content group
            const group = getGroupById(batchSettings, phrase.type as string);
            const maximumAmount: number | undefined = group?.phrases.maxPhrases;

            const isOverIndex = maximumAmount && index + 1 > maximumAmount;

            if (isOverIndex || isEmptyPhrase) {
              deletes.push({
                job_id: String(jobId),
                phrase_id: phrase.id,
                type: phrase.type as PhraseType,
              });
            } else {
              updates.push({
                phrase_id: phrase.id,
                phrase: phrase.phrase,
                job_id: String(jobId),
                type: phrase.type as PhraseType,
                order: index + 1,
              });
            }
          } else if (!isEmptyPhrase) {
            creates.push({
              phrase: phrase.phrase,
              job_id: String(jobId),
              type: phrase.type as PhraseType,
              order: index + 1,
            });
          }
        });
      });

      // Delete phrases that were in the original set but no longer exist in jobPhrases
      // This handles the case where phrases are replaced (e.g., via copy from keyword search)
      const currentPhraseIds = new Set(
        jobPhrases.filter(p => p.id).map(p => p.id!)
      );

      originalPhrases.forEach((phraseType, originalId) => {
        if (!currentPhraseIds.has(originalId)) {
          deletes.push({
            job_id: String(jobId),
            phrase_id: originalId,
            type: phraseType,
          });
        }
      });

      // Execute all operations in a single unified request
      if (creates.length > 0 || updates.length > 0 || deletes.length > 0) {
        await unifiedJobPhrasesOperations({
          creates: creates.length > 0 ? creates : undefined,
          updates: updates.length > 0 ? updates : undefined,
          deletes: deletes.length > 0 ? deletes : undefined,
        });
      }

      if (
        job?.status === JobStatus.TO_BE_STARTED &&
        !statusModified &&
        user?.isCopywriter
      ) {
        await updateJobStatus({
          id: String(jobId),
          status: JobStatus.IN_PROGRESS,
        });

        await refreshJob();
        await refreshBatch();

        setStatusModified(true);
      }

      // Refresh the phrases from the server
      await refreshJobPhrases();
      await setRefreshSearch(true);
      await setHasChanges(false);
      await createJobBackup({ jobId });

      // Refresh advanced stats
      await refreshAdvancedStats();

      return true;
    } catch (error) {
      open?.({
        message: "Error saving phrases.",
        type: "error",
      });
      // Refresh to get current server state and reset originalPhrases
      // This prevents cascade deletions based on stale state
      await refreshJobPhrases();
    } finally {
      setSavingPhrases(false);
    }

    return false;
  };

  const [centralBox, setCentralBox] = useState<
    "bps" | "phrases" | "generation"
  >("bps");

  useEffect(() => {
    if (activeField || searchTab === "jobs") {
      setCentralBox("phrases");
    }

    if (isGenerating) {
      setCentralBox("generation");
    }

    if (!isGenerating && !activeField && searchTab !== "jobs") {
      setCentralBox("bps");
    }
  }, [activeField, searchTab, isGenerating]);

  const titleTemplates = batch?.settings?.title_templates || [];

  return (
    <>
      {!user?.isAdmin && !user?.isClientRelated && (
        <Tracker jobId={String(jobId)} />
      )}

      <Dialog
        open={showNavigationModal}
        onClose={() => setShowNavigationModal(false)}
        aria-labelledby="navigation-dialog-title"
        aria-describedby="navigation-dialog-description"
      >
        <DialogTitle id="navigation-dialog-title">Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText id="navigation-dialog-description">
            There are unsaved changes. If you continue, you will lose all
            unsaved changes.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: "space-between" }}>
          <Button
            onClick={async () => {
              const saved = await handleSavePhrases();
              if (saved) {
                setShowNavigationModal(false);
                setHasChanges(false);

                if (pendingNavigation) {
                  navigation.push(pendingNavigation);
                }
              }
            }}
            color="success"
            variant="contained"
            autoFocus
            disabled={savingPhrases}
          >
            Save and Continue
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={() => {
                setShowNavigationModal(false);
                setHasChanges(false);

                if (pendingNavigation) {
                  navigation.push(pendingNavigation);
                }
              }}
              color="error"
              variant="outlined"
              disabled={savingPhrases}
            >
              Discard Changes
            </Button>
            <Button
              onClick={() => setShowNavigationModal(false)}
              color="primary"
              variant="outlined"
              disabled={savingPhrases}
            >
              Cancel
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Custom Bullet Point Dialog */}
      <Dialog
        open={showCustomBpDialog}
        onClose={() => setShowCustomBpDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Custom Bullet Point</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Custom Name"
            fullWidth
            variant="outlined"
            value={customBpName}
            onChange={(e) => setCustomBpName(e.target.value)}
            placeholder="e.g., Care Instructions, Heel Height"
          />
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Description
              </Typography>
              <Tooltip
                title={
                  customBpName.trim()
                    ? "Generate description with AI"
                    : "Enter a custom name first"
                }
              >
                <span>
                  <Button
                    onClick={async () => {
                      if (!customBpName.trim()) return;
                      setIsGeneratingCustomBpDescription(true);
                      try {
                        const response = await generateBulletPointDescription({
                          bulletPointName: customBpName.trim(),
                          sampleValues: [],
                        });
                        const description = (
                          response.data?.data?.description || ""
                        ).slice(0, 500);
                        setCustomBpDescription(description);
                      } catch (err) {
                        console.error("Failed to generate description:", err);
                      } finally {
                        setIsGeneratingCustomBpDescription(false);
                      }
                    }}
                    disabled={
                      !customBpName.trim() || isGeneratingCustomBpDescription
                    }
                    variant="outlined"
                    size="small"
                    startIcon={
                      isGeneratingCustomBpDescription ? (
                        <CircularProgress size={14} />
                      ) : (
                        <AutoAwesome sx={{ fontSize: 16 }} />
                      )
                    }
                    sx={{ textTransform: "none" }}
                  >
                    AI Generate
                  </Button>
                </span>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              value={customBpDescription}
              onChange={(e) => setCustomBpDescription(e.target.value)}
              placeholder="e.g., The heel height measurement in centimeters"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCustomBpDialog(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              if (customBpName.trim()) {
                await createCustomBulletPoint({
                  job_id: String(jobId),
                  variants: ["New value"],
                  custom_name: customBpName.trim(),
                  custom_description: customBpDescription.trim() || undefined,
                });
                await refreshBulletPoints();
                setShowCustomBpDialog(false);
                setCustomBpName("");
                setCustomBpDescription("");
              }
            }}
            variant="contained"
            disabled={!customBpName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "1rem",
          height: "calc(100vh - 64px - 48px)",
          width: "100%",
          "& ::-webkit-scrollbar": {
            width: "1px",
            height: "1px",
            cursor: "pointer",
          },
          "& ::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            cursor: "pointer",
          },
          "& ::-webkit-scrollbar-thumb": {
            background: "rgba(0, 0, 0, 0.3)",
            borderRadius: "1px",
            cursor: "pointer",
          },
          "& ::-webkit-scrollbar-thumb:hover": {
            background: "rgba(0, 0, 0, 0.3)",
            cursor: "pointer",
          },
        }}
      >
        <Box sx={{ width: "65%", height: "100%" }}>
          {/* Control box */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "1rem",
              justifyContent: "space-between",
              alignItems: "center",
              position: "sticky",
              top: 0,
              p: 1,
              backgroundColor: "white",
              zIndex: 1,
              height: "64px",
            }}
          >
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Clear
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (!backToBatchUrl) return;

                  if (hasChanges) {
                    setPendingNavigation(backToBatchUrl);
                    setShowNavigationModal(true);
                  } else {
                    navigation.push(backToBatchUrl);
                  }
                }}
              />

              <ArrowBack
                style={{
                  cursor: previousJobUrl ? "pointer" : "not-allowed",
                  opacity: previousJobUrl ? 1 : 0.3,
                }}
                onClick={() => handlePreviousJobClick()}
              />

              <ArrowForward
                style={{
                  cursor: nextJobUrl ? "pointer" : "not-allowed",
                  opacity: nextJobUrl ? 1 : 0.3,
                }}
                onClick={() => handleNextJobClick()}
              />

              {hasActiveFilters && (
                <Tooltip
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                        Active Filters:
                      </Typography>
                      {activeFilters.map((filter, index) => (
                        <Typography
                          key={index}
                          variant="caption"
                          sx={{ display: "block" }}
                        >
                          {filter}
                        </Typography>
                      ))}
                    </Box>
                  }
                  arrow
                >
                  <FilterList
                    sx={{
                      color: "primary.main",
                      fontSize: "1.25rem",
                      cursor: "default",
                    }}
                  />
                </Tooltip>
              )}
            </Box>

            {user?.isCopywriter && batch?.copywriter_jobs_to_go && (
              <Box
                sx={{
                  display: "flex",
                  gap: "1rem",
                }}
              >
                <Typography variant="subtitle2">
                  {job?.is_editor && job?.is_reviewer ? (
                    <>
                      You are both a <b>copywriter</b> and a <b>reviewer</b>.
                    </>
                  ) : job?.is_editor ? (
                    <>
                      You are a <b>copywriter</b>.
                    </>
                  ) : job?.is_reviewer ? (
                    <>
                      You are a <b>reviewer</b>.
                    </>
                  ) : (
                    <>
                      You are not a <b>copywriter</b> or a <b>reviewer</b>.
                    </>
                  )}
                </Typography>
                <Typography variant="subtitle2">
                  {job?.is_reviewer &&
                    job?.copywriters?.editors &&
                    job.copywriters.editors.length > 0 && (
                      <>
                        Copywriter:{" "}
                        <b>
                          {job.copywriters.editors
                            .map((editor) => `${editor.name} ${editor.surname}`)
                            .join(", ")}
                        </b>
                      </>
                    )}
                  {job?.is_editor &&
                    job?.copywriters?.reviewers &&
                    job.copywriters.reviewers.length > 0 && (
                      <>
                        {" "}
                        Reviewer:{" "}
                        <b>
                          {job.copywriters.reviewers
                            .map(
                              (reviewer) =>
                                `${reviewer.name} ${reviewer.surname}`
                            )
                            .join(", ")}
                        </b>
                      </>
                    )}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {Object.entries(
                    batch.copywriter_jobs_to_go.jobs_by_statuses
                  ).map(([status, count]) => {
                    return (
                      <Chip
                        sx={{
                          color: "white",
                          cursor: "default",
                          borderRadius: 0,
                          fontSize: "0.75rem",
                          backgroundColor: colorByStatus[status as JobStatus],
                        }}
                        size="small"
                        variant={"filled"}
                        label={`${count} - ${statusToLabel(
                          status as JobStatus
                        )}`}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            <EditorStatus
              status={job?.status}
              isUpdatingStatus={isUpdatingStatus}
              onStatusModified={async (newStatus: JobStatus) => {
                try {
                  setIsUpdatingStatus(true);
                  const saved = await handleSavePhrases();

                  if (!saved) {
                    setIsUpdatingStatus(false);
                    return;
                  }

                  setStatusModified(true);

                  await updateJobStatus({
                    id: String(jobId),
                    status: newStatus,
                  });

                  // move to the next job for copywriters once that they've set the job as TO_BE_REVIEWED
                  if (
                    user?.isCopywriter &&
                    newStatus === JobStatus.TO_BE_REVIEWED &&
                    !job?.is_reviewer
                  ) {
                    handleNextJobClick(true);
                  } else {
                    await refreshJob();
                    await refreshBatch();
                  }
                } catch (error) {
                  //
                } finally {
                  setIsUpdatingStatus(false);
                }
              }}
              phrases={jobPhrases as Phrase[]}
              settings={batchSettings}
              wordsToAvoid={batch?.words_to_avoid}
              user={user}
              isEditor={job?.is_editor}
              isReviewer={job?.is_reviewer}
            />
          </Box>

          {/* MASTER FILE AND BP BOXES */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "1rem",
              height: "calc(80% - 64px)",
              paddingBottom: "1rem",
            }}
          >
            <>
              {/* MASTER FILE BOX */}
              <Box
                sx={{
                  flex: 1,
                  overflowX: "hidden",
                  overflowY: "scroll",
                }}
              >
                {!sheetData ? (
                  <Typography
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                      height: "100%",
                      justifyContent: "center",
                      alignItems: "center",
                      flexGrow: 1,
                    }}
                  >
                    <LoadingButton loading />
                  </Typography>
                ) : (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        mb: "0.5rem",
                      }}
                    >
                      <Typography variant="h6">Master File</Typography>
                      <Tooltip title="You can select partial text in any field and copy it. Click anywhere to clear your selection.">
                        <InfoOutlined
                          sx={{
                            fontSize: "1rem",
                            color: "text.secondary",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </Box>

                    {sheetData.items
                      .filter((item: SheetDataItem) => {
                        // Check if the item has a valid value matching the master_id
                        if (!item || !job?.master_id) return false;

                        const masterId = job.master_id.toString().trim();

                        // Check all keys in the item for a match with master_id
                        return Object.entries(item).some(([, value]) => {
                          // Skip null or undefined values
                          if (value === null || value === undefined)
                            return false;
                          return value.toString().trim() === masterId;
                        });
                      })
                      .map((item: SheetDataItem, index: number) => (
                        <MasterFileView
                          key={`${job?.master_id}-${index}`}
                          item={item}
                          bulletPointToCopy={bulletPointToCopy}
                          setBulletPointToCopy={setBulletPointToCopy}
                        />
                      ))}
                  </>
                )}
              </Box>

              <Box
                sx={{
                  flex: 3,
                  overflowY: "scroll",
                  paddingRight: "1rem",
                }}
              >
                {/* BULLET POINTS BOX */}
                {centralBox === "phrases" && (
                  <EditorPhrasebookSearch
                    originalLanguage={batch?.original_language}
                    settings={batchSettings}
                    brandsWithGlobalPhrases={brandsWithGlobalPhrases}
                    defaultSelectedBrands={defaultSelectedBrands}
                    updatePhrasesByType={(type, phrases, append) => {
                      const filteredPhrases = jobPhrases.filter(
                        (phrase) => phrase && phrase.type === type
                      );

                      const newPhrases = [...filteredPhrases];

                      if (append) {
                        // When appending, find the first empty phrase or use the last position
                        const emptyIndex = newPhrases.findIndex(
                          (phrase) =>
                            !phrase.phrase || phrase.phrase.trim() === ""
                        );

                        // Only append the first phrase from the provided array when using append mode
                        if (phrases.length > 0 && phrases[0]) {
                          // Handle empty array case - push new phrase instead of accessing invalid index
                          if (newPhrases.length === 0) {
                            newPhrases.push({
                              order: 1,
                              phrase: phrases[0].phrase,
                              type,
                              status: phrases[0].status,
                            });
                          } else {
                            // Find insert position - either empty slot or last position
                            let insertIndex =
                              emptyIndex !== -1 ? emptyIndex : newPhrases.length - 1;

                            // Ensure index is within bounds
                            insertIndex = Math.max(0, Math.min(insertIndex, newPhrases.length - 1));

                            // Replace an empty phrase - clear id to treat as new phrase
                            newPhrases[insertIndex] = {
                              order: newPhrases[insertIndex].order,
                              phrase: phrases[0].phrase,
                              type,
                              status: phrases[0].status,
                              // id is intentionally omitted - treat as new phrase
                            };
                          }
                        }
                      } else {
                        // Original replace behavior
                        for (let i = 0; i < newPhrases.length; i++) {
                          const oldPhrase = newPhrases[i];

                          if (phrases[i]) {
                            // Clear id to treat as new phrase
                            newPhrases[i] = {
                              order: oldPhrase.order,
                              phrase: phrases[i].phrase,
                              type,
                              status: phrases[i].status,
                              // id is intentionally omitted - treat as new phrase
                            };
                          } else {
                            // Clear id when clearing phrase content
                            newPhrases[i] = {
                              order: oldPhrase.order,
                              phrase: "",
                              type,
                              status: undefined,
                              // id is intentionally omitted
                            };
                          }
                        }
                      }

                      updatePhrasesByType(type, newPhrases);

                      setHasChanges(true);
                    }}
                  />
                )}

                {centralBox === "bps" && (
                  <>
                    {!job || !batch ? (
                      <Typography
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "1rem",
                          height: "100%",
                          justifyContent: "center",
                          alignItems: "center",
                          flexGrow: 1,
                        }}
                      >
                        <LoadingButton loading />
                      </Typography>
                    ) : (
                      <>
                        {batch?.organization_mode_allow_ai_generation === true ? (
                          <>
                            <Typography
                              variant="h6"
                              sx={{
                                mb: "0.5rem",
                              }}
                            >
                              Functional Bullet Points
                            </Typography>

                            <ResponsiveMasonry
                              columnsCountBreakPoints={{
                                100: 1,
                                800: 2,
                                1000: 3,
                              }}
                            >
                              <Masonry>
                                {Object.entries(bulletPointsByType).map(
                                  ([type, bulletPoints]) => (
                                    <BulletPointsList
                                      key={type}
                                      bulletPoint={bulletPoints[0]}
                                      onRefresh={() => refreshBulletPoints()}
                                      setBulletPointToCopy={
                                        setBulletPointToCopy
                                      }
                                      bulletPointToCopy={bulletPointToCopy}
                                    />
                                  )
                                )}

                                {detailBulletPoints &&
                                  detailBulletPoints.length > 0 &&
                                  detailBulletPoints.map((bulletPoint) => (
                                    <BulletPointsList
                                      key={bulletPoint.id}
                                      bulletPoint={bulletPoint}
                                      onRefresh={() => refreshBulletPoints()}
                                      setBulletPointToCopy={
                                        setBulletPointToCopy
                                      }
                                      bulletPointToCopy={bulletPointToCopy}
                                    />
                                  ))}

                                {customBulletPoints &&
                                  customBulletPoints.length > 0 &&
                                  customBulletPoints.map((bulletPoint) => (
                                    <BulletPointsList
                                      key={bulletPoint.id}
                                      bulletPoint={bulletPoint}
                                      onRefresh={() => refreshBulletPoints()}
                                      setBulletPointToCopy={
                                        setBulletPointToCopy
                                      }
                                      bulletPointToCopy={bulletPointToCopy}
                                    />
                                  ))}
                              </Masonry>
                            </ResponsiveMasonry>

                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                mt: 1,
                                justifyContent: "center",
                              }}
                            >
                              <Button
                                variant="text"
                                onClick={async () => {
                                  await createDetailBulletPoint({
                                    variants: ["New detail"],
                                    job_id: String(jobId),
                                  });

                                  await refreshBulletPoints();
                                }}
                              >
                                Add detail bullet point
                              </Button>
                              <Button
                                variant="text"
                                onClick={() => {
                                  setCustomBpName("");
                                  setCustomBpDescription("");
                                  setShowCustomBpDialog(true);
                                }}
                              >
                                Add custom bullet point
                              </Button>
                            </Box>

                            <Box
                              sx={{
                                py: 16,
                              }}
                            />
                          </>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "100%",
                              width: "100%",
                            }}
                          >
                            <img
                              src={logo}
                              alt="Fashion Words Logo"
                              style={{
                                maxWidth: "300px",
                                height: "auto",
                                objectFit: "contain",
                                opacity: 0.3,
                              }}
                            />
                          </Box>
                        )}
                      </>
                    )}
                  </>
                )}

                {centralBox === "generation" && (
                  <EditorGeneration
                    titleTemplates={titleTemplates}
                    batchSettings={batchSettings}
                    appendPhrase={(type: PhraseType, phrase: string) => {
                      const filteredPhrases = jobPhrases.filter(
                        (p) => p && p.type === type
                      );

                      const newPhrases = [...filteredPhrases];

                      // Handle empty array case - push new phrase
                      if (newPhrases.length === 0) {
                        newPhrases.push({
                          order: 1,
                          phrase,
                          type,
                          status: PhraseStatus.UNREVIEWED,
                        });
                      } else {
                        const emptyIndex = newPhrases.findIndex(
                          (p) => !p.phrase || p.phrase.trim() === ""
                        );

                        // Find insert position - either empty slot or last position
                        let insertIndex =
                          emptyIndex !== -1 ? emptyIndex : newPhrases.length - 1;

                        // Ensure index is within bounds
                        insertIndex = Math.max(0, Math.min(insertIndex, newPhrases.length - 1));

                        newPhrases[insertIndex] = {
                          ...newPhrases[insertIndex],
                          phrase,
                          type,
                          status: PhraseStatus.UNREVIEWED,
                        };
                      }

                      updatePhrasesByType(type, newPhrases);

                      setHasChanges(true);
                    }}
                    replaceAllPhrases={(
                      type: PhraseType,
                      phrases: string[]
                    ) => {
                      const filteredPhrases = jobPhrases.filter(
                        (phrase) => phrase && phrase.type === type
                      );

                      const newPhrases = [...filteredPhrases];

                      // Replace phrases up to the length of generated phrases
                      for (let i = 0; i < newPhrases.length; i++) {
                        const oldPhrase = newPhrases[i];

                        if (phrases[i]) {
                          newPhrases[i] = {
                            ...oldPhrase,
                            phrase: phrases[i],
                            type,
                            status: PhraseStatus.UNREVIEWED,
                          };
                        } else {
                          newPhrases[i] = {
                            ...oldPhrase,
                            phrase: "",
                            type,
                            status: undefined,
                          };
                        }
                      }

                      updatePhrasesByType(type, newPhrases);

                      setHasChanges(true);
                    }}
                  />
                )}
              </Box>
            </>
          </Box>

          {/* IMAGES OVERLAY */}
          {selectedImageIndex !== null && (
            <ImageZoom
              selectedImage={images[selectedImageIndex]}
              bottomContainerHeight={bottomContainerHeight || 0}
              onClose={() => setSelectedImageIndex(null)}
            />
          )}

          {/* IMAGES BOX */}
          <Box
            ref={bottomContainerRef}
            onWheel={(e) => {
              if (bottomContainerRef.current) {
                bottomContainerRef.current.scrollLeft += e.deltaY;
              }
            }}
            sx={{
              width: "100%",
              overflowY: "hidden",
              overflowX: "scroll",
              height: "20%",
            }}
          >
            {images && Array.isArray(images) && images.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                  alignItems: "center",
                  height: "100%",
                }}
                onMouseOver={(e) => {
                  const isTargetImg: boolean =
                    e.target instanceof HTMLImageElement;

                  if (!isTargetImg) {
                    setSelectedImageIndex(null);
                  }
                }}
              >
                {images.map((image, index) => (
                  <img
                    key={index}
                    onMouseEnter={() => {
                      if (imageEnterTimeout) {
                        clearTimeout(imageEnterTimeout);
                      }

                      if (selectedImageIndex !== null) {
                        setSelectedImageIndex(index);
                      } else {
                        imageEnterTimeout = setTimeout(() => {
                          setSelectedImageIndex(index);
                        }, 500);
                      }
                    }}
                    onMouseLeave={() => {
                      if (imageEnterTimeout) {
                        clearTimeout(imageEnterTimeout);
                      }
                    }}
                    src={image}
                    alt={`Product image ${index + 1}`}
                    style={{
                      width: "auto",
                      height: bottomContainerHeight
                        ? `${bottomContainerHeight}px`
                        : "auto",
                      objectFit: "contain",
                      flexShrink: 0,
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No images available
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* TITLE AND EDITORIAL DESCRIPTION BOX */}
        <Box
          onMouseEnter={() => {
            setSelectedImageIndex(null);
          }}
          sx={{
            width: "35%",
            height: "100%",
            overflowY: "scroll",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            paddingRight: "1rem",
          }}
        >
          {job?.id && batch?.id ? (
            <EditorPhrasebookEditor
              jobId={job?.id || ""}
              batchId={batch?.id || ""}
              settings={batchSettings}
              wordsToAvoid={batch?.words_to_avoid}
              jobPhrases={jobPhrases}
              setJobPhrases={setJobPhrases}
              updatePhrasesByType={updatePhrasesByType}
              handleSavePhrases={handleSavePhrases}
              advancedStats={advancedStats}
              readOnly={
                (job?.is_editor && job?.status === JobStatus.TO_BE_REVIEWED) ||
                job?.status === JobStatus.DELIVERED ||
                job?.status === JobStatus.COMPLETED
              }
              hasBulletPoints={
                notDetailBulletPoints.some((bp) => bp.variants.length > 0) ||
                customBulletPoints.some((bp) => bp.variants.length > 0)
              }
              isAIEnabled={batch?.organization_mode_allow_ai_generation === true}
            />
          ) : (
            <LoadingButton
              sx={{
                display: "flex",
                height: "100%",
                justifyContent: "center",
              }}
              loading
            />
          )}

          <Box
            sx={{
              py: 16,
            }}
          />
        </Box>
      </Box>
    </>
  );
}
